import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ArrowLeft, Check, FileText } from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { StoreHeader } from "../components/StoreHeader";
import { useCart } from "../context/CartContext";
import { formatINR, resolveImg } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const { add, setOpen } = useCart();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [diameter, setDiameter] = useState("");
  const [notFound, setNotFound] = useState(false);

  const isTmt = p && p.category === "tmt";
  const TMT_SIZES = ["8", "10", "12", "16", "20", "25", "28", "32", "40"];

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then((r) => { setP(r.data); setQty(r.data.moq || 1); }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]"><StoreHeader />
      <div className="max-w-[1600px] mx-auto px-6 py-24 text-center"><p className="font-body text-[#8A8A82]">Product not found.</p><Link to="/shop" className="text-[#B87333]">Back to shop</Link></div>
    </div>
  );
  if (!p) return <div className="min-h-screen bg-[#070707] grid place-items-center"><div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-[#B87333] animate-spin" /></div>;

  const addToCart = () => { add(p, qty); toast.success(`Added ${qty} × ${p.name}`); };
  const pending = p.price_pending || !p.price;
  const rfqLink = `/request-quote?material=${encodeURIComponent(p.name)}&unit=${encodeURIComponent(p.uom || "")}&grade=${encodeURIComponent(p.grade || "")}`;

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]">
      <Toaster position="bottom-right" theme="dark" richColors />
      <StoreHeader />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-10">
        <Link to="/shop" className="inline-flex items-center gap-2 font-grotesk text-xs uppercase tracking-[0.2em] text-[#8A8A82] hover:text-[#F5F5F0] transition-colors mb-10">
          <ArrowLeft size={14} /> Back to shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <motion.div initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative aspect-square overflow-hidden rounded-sm border border-white/10">
            <img src={resolveImg(p.image)} alt={p.name} className={`w-full h-full object-cover ${p.in_stock === false ? "grayscale opacity-60" : ""}`} />
            <span className="absolute top-5 left-5 font-grotesk text-[10px] uppercase tracking-[0.2em] bg-black/60 backdrop-blur px-4 py-1.5 rounded-full border border-white/10">{p.category_label}</span>
            {p.in_stock === false && <span className="absolute top-5 right-5 font-grotesk text-[10px] uppercase tracking-[0.2em] bg-[#C0392B] text-white px-4 py-1.5 rounded-full">Out of stock</span>}
          </motion.div>

          <div>
            <div className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-[#B87333]">{p.brand}</div>
            <h1 className="font-serif text-4xl md:text-6xl mt-3 tracking-[-0.02em]">{p.name}</h1>
            <div className="flex items-baseline gap-3 mt-6">
              {pending ? (
                <span className="font-serif text-3xl text-[#C9C9C2]" data-testid="price-pending">Price on request</span>
              ) : (
                <span className="font-serif text-4xl">{formatINR(p.price)}</span>
              )}
              <span className="font-body text-[#8A8A82]">{p.unit}{p.hsn ? ` · HSN ${p.hsn}` : ""}</span>
            </div>
            {p.phase && (
              <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] mt-2" data-testid="product-phase">Phase {p.phase} · {p.phase_label || p.category_label}</div>
            )}
            {p.price_low > 0 && !pending && (
              <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] mt-2" data-testid="price-range">Indicative market range ₹{p.price_low.toLocaleString("en-IN")}–₹{p.price_high.toLocaleString("en-IN")} {p.unit}</div>
            )}
            <p className="font-body text-[#C9C9C2] leading-relaxed mt-6 max-w-lg">{p.description}</p>

            <div className="mt-8 border-t border-white/10">
              {Object.entries(p.specs || {}).map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-6 py-3.5 border-b border-white/10">
                  <span className="font-grotesk text-[11px] uppercase tracking-[0.18em] text-[#8A8A82]">{k}</span>
                  <span className="font-body text-sm text-[#F5F5F0] text-right max-w-[60%]">{v}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-6 text-[#8A8A82] font-body text-sm">
              <Check size={15} className="text-[#2FB999]" /> Min. order {p.moq} {p.unit.replace("per ", "")}{p.moq > 1 ? "s" : ""}
            </div>

            {pending ? (
              <div className="mt-8 border border-[#B87333]/30 bg-[#B87333]/5 rounded-sm p-6" data-testid="rfq-panel">
                <span className="overline text-[#B87333] block">Priced on request</span>
                <p className="font-body text-sm text-[#C9C9C2] mt-2">This master-catalogue line is quoted project-by-project. Raise an RFQ and the Vinci trade desk reverts with tiered, GST-compliant pricing.</p>
                <Link data-testid="raise-rfq" to={rfqLink} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#B87333] text-black px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:opacity-90 transition-opacity">
                  <FileText size={14} /> Raise RFQ for {p.name}
                </Link>
              </div>
            ) : (
            <>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <div className="flex items-center border border-white/20 rounded-full">
                <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(p.moq || 1, q - 1))} className="w-12 h-12 grid place-items-center hover:text-[#B87333]"><Minus size={16} /></button>
                <input
                  data-testid="qty-value"
                  type="number"
                  min={p.moq || 1}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.floor(Number(e.target.value) || 0)))}
                  onBlur={(e) => { const v = Math.floor(Number(e.target.value) || 0); setQty(Math.max(p.moq || 1, v)); }}
                  className="w-16 text-center bg-transparent outline-none font-serif text-xl text-[#F5F5F0] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="w-12 h-12 grid place-items-center hover:text-[#B87333]"><Plus size={16} /></button>
              </div>
              <button data-testid="add-to-cart" disabled={p.in_stock === false} onClick={addToCart} className="flex-1 min-w-[200px] rounded-full bg-[#F5F5F0] text-black py-4 px-8 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors disabled:opacity-40">
                {p.in_stock === false ? "Out of stock" : `Add to cart · ${formatINR(p.price * qty)}`}
              </button>
            </div>
            {p.in_stock !== false && (
              <button data-testid="buy-now" onClick={() => { add(p, qty); setOpen(false); nav("/checkout"); }} className="mt-4 w-full rounded-full border border-white/20 py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">
                Buy now
              </button>
            )}
            <Link data-testid="raise-rfq" to={rfqLink} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#B87333]/60 text-[#B87333] py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] hover:text-black transition-colors">
              <FileText size={14} /> Request a project quote · {p.grade}
            </Link>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
